import React from 'react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'framer-motion';

import { cn } from 'shared/lib/utils';

type DivMotionProps = HTMLMotionProps<'div'>;

interface GlassCardProps extends Omit<DivMotionProps, 'children'> {
  /**
   * Tone affects the inner gradient overlay. `default` is white-ish glass,
   * `accent` adds a subtle haat-red wash for hero / success surfaces.
   */
  tone?: 'default' | 'accent';
  /** Disable the entrance animation (e.g. when nested inside another animated wrapper). */
  noEnter?: boolean;
  /** Optional classes for the inner content wrapper. */
  contentClassName?: string;
  children: React.ReactNode;
}

/**
 * Premium glassmorphism surface used as the visual base for onboarding cards.
 * Combines a translucent background, a soft border, a layered drop shadow
 * and a subtle inner highlight to read as frosted glass on the gradient
 * AppShell background.
 */
const GlassCard: React.FC<GlassCardProps> = ({
  tone = 'default',
  noEnter = false,
  contentClassName,
  className,
  children,
  ...rest
}) => {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      initial={noEnter || reduceMotion ? false : { opacity: 0, y: 24, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -16, scale: 0.99 }}
      transition={{ duration: reduceMotion ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        'relative overflow-hidden rounded-[28px]',
        'border border-white/60',
        'bg-white/75 backdrop-blur-xl backdrop-saturate-150',
        'shadow-[0_30px_80px_-30px_rgba(17,24,39,0.25),0_8px_24px_-12px_rgba(229,57,53,0.18)]',
        className,
      )}
      {...rest}
    >
      {/* Inner top highlight */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80"
      />
      {/* Tonal wash */}
      {tone === 'accent' && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-24 -end-24 h-64 w-64 rounded-full bg-haat-red-500/15 blur-3xl"
        />
      )}
      <div className={cn('relative', contentClassName)}>{children}</div>
    </motion.div>
  );
};

export default GlassCard;
